import { Box } from '@mui/material';
import type { ContentRef, InsightRenderer, MetricRenderer } from '../types';

type Props = {
  renderer?: MetricRenderer | InsightRenderer;
  span?: ContentRef['span'];
  hasHeading?: boolean;
  barColor: string;
};

const bar = (
  color: string,
  width: string | number | Record<string, string | number>,
  height: number,
  extra?: object,
) => ({
  height,
  width,
  borderRadius: 1,
  bgcolor: color,
  ...extra,
});

/** Blurred ghost chrome matching the live metric/insight renderer layout. */
export default function TileErrorSkeleton({
  renderer,
  span,
  hasHeading = false,
  barColor,
}: Props) {
  const topOffset = hasHeading ? 52 : 8;

  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        top: topOffset,
        left: 16,
        right: 16,
        bottom: 40,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: renderer === 'verticalBarChart' ? 'flex-end' : 'flex-start',
        gap: 1.25,
        opacity: 0.9,
        filter: 'blur(1px)',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    >
      {renderSkeleton(renderer, span, barColor)}
    </Box>
  );
}

function renderSkeleton(
  renderer: MetricRenderer | InsightRenderer | undefined,
  span: ContentRef['span'] | undefined,
  color: string,
) {
  switch (renderer) {
    case 'hero':
    case 'heroWithAlert':
      return (
        <>
          <Box sx={bar(color, { xs: '48%', sm: '32%' }, 56)} />
          <Box sx={bar(color, '62%', 14)} />
          <Box sx={bar(color, 96, 22, { borderRadius: 4, mt: 0.5 })} />
        </>
      );

    case 'statCard':
    case 'statCardWithBadge':
      return (
        <>
          <Box sx={bar(color, '46%', 36)} />
          <Box sx={{ mt: 'auto' }}>
            <Box sx={bar(color, '58%', 12)} />
          </Box>
        </>
      );

    case 'statCardWithDot':
      return (
        <>
          <Box sx={bar(color, '42%', 32)} />
          <Box sx={{ mt: 'auto' }}>
            <Box sx={bar(color, '36%', 10)} />
          </Box>
        </>
      );

    case 'statWithDescription':
      return (
        <>
          <Box sx={bar(color, '44%', 40)} />
          <Box sx={bar(color, '88%', 12)} />
          <Box sx={bar(color, '70%', 12)} />
        </>
      );

    case 'statWithBreakdown':
      return (
        <>
          <Box sx={bar(color, '44%', 40)} />
          <Box sx={bar(color, '80%', 12)} />
          {[ '72%', '58%', '64%' ].map(width => (
            <Box
              key={width}
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}
            >
              <Box sx={bar(color, width, 10)} />
              <Box sx={bar(color, 36, 10, { flexShrink: 0 })} />
            </Box>
          ))}
        </>
      );

    case 'benchmarkGauge':
      return (
        <>
          <Box sx={bar(color, '40%', 36)} />
          <Box sx={{ mt: 'auto' }}>
            <Box sx={bar(color, '100%', 8, { borderRadius: 2 })} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
              <Box sx={bar(color, 72, 8)} />
              <Box sx={bar(color, 56, 8)} />
            </Box>
            <Box sx={bar(color, 96, 18, { borderRadius: 4, mt: 1 })} />
          </Box>
        </>
      );

    case 'rankedBreakdown':
      if (span === 'full') {
        return (
          <>
            {[ '38%', '52%', '44%', '48%', '36%' ].map((width, i) => (
              <Box
                key={i}
                sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
              >
                <Box sx={bar(color, width, 12, { flexShrink: 0, minWidth: 80 })} />
                <Box sx={bar(color, 32, 12, { flexShrink: 0 })} />
                <Box sx={bar(color, `${40 + i * 10}%`, 6, { borderRadius: 2, flex: 1 })} />
              </Box>
            ))}
          </>
        );
      }
      return (
        <>
          {[ 72, 54, 46, 38 ].map((pct, i) => (
            <Box key={i} sx={{ mb: 0.25 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, gap: 1 }}>
                <Box sx={bar(color, `${pct}%`, 12)} />
                <Box sx={bar(color, 40, 12, { flexShrink: 0 })} />
              </Box>
              <Box sx={bar(color, `${pct}%`, 6, { borderRadius: 2 })} />
            </Box>
          ))}
        </>
      );

    case 'dataTable':
      return (
        <>
          <Box sx={{ display: 'flex', gap: 2, mb: 0.5 }}>
            {[ '28%', '22%', '18%', '16%' ].map(width => (
              <Box key={width} sx={bar(color, width, 10)} />
            ))}
          </Box>
          {[ 0, 1, 2, 3, 4 ].map(i => (
            <Box key={i} sx={{ display: 'flex', gap: 2 }}>
              <Box sx={bar(color, '28%', 12)} />
              <Box sx={bar(color, '18%', 12)} />
              <Box sx={bar(color, '14%', 12)} />
              <Box sx={bar(color, '16%', 12)} />
            </Box>
          ))}
        </>
      );

    case 'verticalBarChart':
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-around',
            gap: 1.5,
            height: '100%',
            px: 1,
          }}
        >
          {[ 48, 78, 36, 62, 44 ].map((h, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.75,
                flex: 1,
                height: '100%',
                justifyContent: 'flex-end',
              }}
            >
              <Box sx={bar(color, '70%', h, { borderRadius: '4px 4px 0 0' })} />
              <Box sx={bar(color, '80%', 8)} />
            </Box>
          ))}
        </Box>
      );

    case 'sectionTitleMetric':
      return <Box sx={bar(color, '42%', 16)} />;

    case 'insightList':
      return (
        <>
          {[ 0, 1, 2 ].map(i => (
            <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 0.5 }}>
              <Box sx={bar(color, 22, 22, { borderRadius: '50%', flexShrink: 0 })} />
              <Box sx={{ flex: 1 }}>
                <Box sx={bar(color, '70%', 12, { mb: 0.75 })} />
                <Box sx={bar(color, '90%', 10)} />
              </Box>
            </Box>
          ))}
        </>
      );

    case 'numberedInsightList':
      return (
        <>
          {[ 0, 1, 2 ].map(i => (
            <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 0.5 }}>
              <Box sx={bar(color, 20, 20, { flexShrink: 0 })} />
              <Box sx={{ flex: 1 }}>
                <Box sx={bar(color, '64%', 12, { mb: 0.75 })} />
                <Box sx={bar(color, '88%', 10)} />
              </Box>
            </Box>
          ))}
        </>
      );

    case 'journeyTimeline':
      return (
        <>
          <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
            {[ 0, 1, 2, 3 ].map(i => (
              <Box key={i} sx={{ flex: 1 }}>
                <Box sx={bar(color, '70%', 8, { mb: 0.75 })} />
                <Box sx={bar(color, '50%', 18)} />
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
            {[ 0, 1, 2, 3 ].map(i => (
              <Box key={i} sx={bar(color, 14, 14, { borderRadius: '50%' })} />
            ))}
          </Box>
        </>
      );

    case 'narrativeBlock':
      return (
        <>
          <Box sx={bar(color, '28%', 10)} />
          <Box sx={bar(color, '74%', 20)} />
          <Box sx={bar(color, '92%', 12)} />
          <Box sx={bar(color, '80%', 12)} />
        </>
      );

    default:
      return (
        <>
          <Box sx={bar(color, '42%', 36)} />
          <Box sx={bar(color, '68%', 12)} />
          <Box sx={bar(color, '54%', 12)} />
        </>
      );
  }
}
